<template>
  <div class="foundation">
    <b-container
      class="foundation-container"
      fluid
      style="padding: 0px !important;"
    >
      <b-row class="no-gutters">
        <b-col class="d-flex flex-column" sm="5">
          <div class="foundation-header d-flex p-2">
            <level-dropdown></level-dropdown>

            <div
              class="align-middle d-flex align-items-center foundation-header-title"
            >
              Excercise {{ level.level }}: {{ level.title }}
            </div>
          </div>
          <div class="foundation-content">
            <h2>Introduction to Functions</h2>
            <p>
              Even a simple computer program contains hundreds if not thousands
              lines of codes. Most of the time, part of these codes are
              repeated. Instead of typing out the same instructions over and
              over, we can group then into chunks called function or procedure.
            </p>
            <img width="250" :src="`${publicPath}img/function_one.png`" />
            <p>
              Above we have a function call <code>saySomething</code>. Whenever
              we run this function, all the code within
              <code>saySomething</code> will be executed.
            </p>
            <img width="200" :src="`${publicPath}img/function_one_2.png`" />
            <p>
              We can reuse the function as many times as we want without writing
              the same code again and again. In example above the function was
              called twice so code in <code>saySomething</code> will execute for
              2 times
            </p>
            <div class="activity-header my-3">
              Activity
            </div>
            <ol>
              <li>
                Create a function <span class="variable">introduceMyself</span>
              </li>
              <li>
                The function will print 3 messages in following sequence when
                called
                <ul>
                  <li><span class="string">Hi!</span></li>
                  <li><span class="string">I am a bee.</span></li>
                  <li><span class="string">I like coding.</span></li>
                </ul>
              </li>
              <li>
                Call <span class="variable">introduceMyself</span> function once
              </li>
            </ol>
          </div>
        </b-col>
        <b-col>
          <div class="foundation-actionbar d-flex justify-content-end">
            <b-button class="my-auto mr-2" @click="viewCode">
              View Code
            </b-button>
            <b-button
              variant="danger"
              class="my-auto mr-2"
              @click="resetWorkspace"
            >
              Reset
            </b-button>
            <b-button variant="primary" class="my-auto mr-2" @click="runCode">
              <font-awesome-icon
                class="mr-2"
                :icon="['fas', 'play']"
                style="color: white;"
              />Run
            </b-button>
          </div>
          <div id="blocklyDiv"></div>
        </b-col>
      </b-row>
    </b-container>

    <b-modal v-model="showCode" title="JavaScript Code">
      <p class="my-4" style="white-space:pre-wrap;">
        {{ code }}
      </p>
    </b-modal>

    <b-modal
      v-model="showResultModal"
      title="Code result"
      @ok="onClickResultModalOk"
    >
      <div class="text-center">
        <h3>
          {{ isResultCorrect ? "Correct" : "Wrong" }}
        </h3>
        <p class="my-4" style="white-space:pre-wrap;">
          {{ resultMessage }}
        </p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import LevelDropdown from "@/components/LevelDropdown.vue";
import Blockly from "blockly";
import Interpreter from "js-interpreter";
import LevelRepository from "@/repositories/level-repository";

import UserServices from "@/services/user-services";
import { mapState } from "vuex";

export default {
  name: "level-two",

  components: {
    LevelDropdown
  },

  data() {
    return {
      id: this.$route.params.id,
      publicPath: process.env.BASE_URL,
      workspace: null,
      code: "",
      showCode: false,
      showResultModal: false,
      isResultCorrect: false,
      resultMessage: "",
      levels: [],
      level: {
        level: "",
        title: "",
        toolbox: null
      },
      result: {
        counter: 0,
        text: ""
      }
    };
  },

  mounted() {
    this.getLevels();
    this.getLevel();
    this.injectBlockly();
  },

  computed: {
    ...mapState(["user"])
  },

  methods: {
    initPage() {
      this.removeBlocklyInjection();
      this.getLevels();
      this.getLevel();
      this.injectBlockly();
    },

    injectBlockly() {
      this.workspace = Blockly.inject(document.getElementById("blocklyDiv"), {
        trashcan: true,
        toolbox: this.level.toolbox
      });

      if (this.level.workspace != undefined) {
        let workspaceBlock = Blockly.Xml.textToDom(this.level.workspace);
        Blockly.Xml.domToWorkspace(workspaceBlock, this.workspace);
      }
    },

    getLevel() {
      this.level = LevelRepository.getLevel(this.id);
    },

    getLevels() {
      this.levels = LevelRepository.getLevels();
    },

    viewCode() {
      this.code = Blockly.JavaScript.workspaceToCode(this.workspace);
      this.showCode = true;
    },

    resetWorkspace() {
      if (confirm("Are you sure you want to reset this question?")) {
        this.result = {};
        document.getElementById("blocklyDiv").innerHTML = "";
        this.injectBlockly();
      }
    },

    removeBlocklyInjection() {
      document.getElementById("blocklyDiv").innerHTML = "";
    },

    runCode() {
      let workspaceCode = Blockly.JavaScript.workspaceToCode(this.workspace);
      let code = this.appendCode(workspaceCode);
      this.resetResult();
      let myInterpreter = new Interpreter(code, this.getInitApi());
      myInterpreter.run();
      this.checkResult(code);
    },

    appendCode(code) {
      code += "try {returnResult();} catch (err) {};";
      return code;
    },

    getInitApi() {
      const self = this;
      return (interpreter, scope) => {
        const wrapper = function() {};
        interpreter.setProperty(
          scope,
          "returnResult",
          interpreter.createNativeFunction(wrapper)
        );

        const alertWrapper = function(text) {
          alert(arguments.length ? text.data : "");
          self.result.isAlertCalled = true;
          self.result.text += text;
          self.result.counter++;
        };

        interpreter.setProperty(
          scope,
          "alert",
          interpreter.createNativeFunction(alertWrapper)
        );
      };
    },

    checkResult(code) {
      const self = this;
      self.isResultCorrect = false;

      if (!code.includes("function introduceMyself()")) {
        this.resultMessage = "Functions is not created correctly";
      } else if (!code.includes("introduceMyself();")) {
        this.resultMessage = "Function not called";
      } else if (this.result.text != "Hi!I am a bee.I like coding.") {
        this.resultMessage = "Statement not printed correctly";
      } else {
        self.isResultCorrect = true;
        self.resultMessage = "Great! Let move on to the next task.";
        if (this.user.uid != "") {
          this.updateUserDoneActivity();
        }
      }
      self.showResultModal = true;
    },

    onClickResultModalOk() {
      if (this.isResultCorrect) {
        let nextLevel = parseInt(this.id) + 1;
        this.$router.push("/foundation/" + nextLevel);
      } else {
        this.showResultModal = false;
      }
    },

    async updateUserDoneActivity() {
      await UserServices.updateUserDoneActivity(
        this.user.uid,
        this.level.firebaseId,
        "foundation"
      );
    },

    resetResult() {
      this.result = {
        counter: 0,
        text: ""
      };
    }
  },

  beforeRouteUpdate(to, from, next) {
    this.id = to.params.id;
    this.initPage();
    next();
  }
};
</script>

<style lang="less" scoped>
.foundation {
  &-header {
    background-color: rgb(255, 223, 137);

    &-title {
      line-height: 1.25rem;
      font-size: 1.25rem;
    }
  }

  &-content {
    padding: 15px;
    overflow: auto;
    height: calc(100vh - 120px);
  }

  &-actionbar {
    height: 54px;
  }
}

#blocklyDiv {
  height: calc(100vh - 110px);
  width: 100%;
}
</style>
